import React, { useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View, ViewStyle } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { MODULE_COLORS } from '@cubemeet/core';

/**
 * 待办事项元数据
 */
export interface TodoMetadata {
  isAd?: boolean;
  adPartner?: string;
  adUrl?: string;
  adCampaign?: string;
  cta?: string;
  [key: string]: any;
}

/**
 * 待办事项数据接口
 */
export interface TodoItem {
  id: string;
  title: string;
  description?: string;
  completed: boolean;
  creator?: {
    display_name?: string;
  };
  createdAt?: string;
  created_by?: string;
  created_at?: string;
  metadata?: TodoMetadata; // 元数据字段
}

/**
 * TodoManagement 组件属性
 */
export interface TodoManagementProps {
  /**
   * 待办事项列表
   */
  todos?: TodoItem[];
  /**
   * 是否正在加载
   */
  isLoading?: boolean;
  /**
   * 是否可以添加待办事项
   */
  canAddTodo?: boolean;
  /**
   * 添加待办事项回调
   */
  onAddTodo?: () => void;
  /**
   * 切换待办事项完成状态回调
   */
  onToggleTodo?: (todoId: string, completed: boolean) => void;
  /**
   * 广告点击回调
   */
  onAdClick?: (adUrl: string, adPartner: string, todoId: string) => void;
  /**
   * 翻译函数
   */
  t?: (key: string, params?: Record<string, any>) => string;
  /**
   * 自定义样式
   */
  style?: ViewStyle;
  /**
   * 是否显示卡片容器（默认 true）
   */
  showCard?: boolean;
  /**
   * 初始展开数量（默认 3）
   */
  initialVisibleCount?: number;
  /**
   * 可选：为内部待办列表容器提供自定义样式
   */
  todoListStyle?: ViewStyle;
}

const defaultT = (key: string, params?: Record<string, any>) => {
  // 简单的翻译回退
  const translations: Record<string, string> = {
    'travelDetail.content.todoList': '待办事项',
    'travelDetail.content.add': '添加',
    'travelDetail.content.noTodos': '暂无待办事项',
    'travelDetail.content.noTodosSubtext': '添加待办事项来规划旅行',
    'travelDetail.content.collapse': '收起',
    'travelDetail.content.expandWithCount': `展开更多 (${params?.count || 0})`,
    'travelDetail.content.completed': '已完成',
    'travelDetail.content.unknownUser': '未知用户',
  };
  return translations[key] || key;
};

/**
 * TodoManagement 组件
 * 
 * 用于显示和管理待办事项列表的共享组件
 */
const TodoManagement: React.FC<TodoManagementProps> = ({
  todos = [],
  isLoading = false,
  canAddTodo = true,
  onAddTodo,
  onToggleTodo,
  onAdClick,
  t = defaultT,
  style,
  showCard = true,
  initialVisibleCount = 3,
  todoListStyle,
}) => {
  const [isTodosExpanded, setIsTodosExpanded] = useState(false);

  const handleAddTodo = () => {
    console.log('[TodoManagement] handleAddTodo clicked');
    if (onAddTodo) {
      try {
        onAddTodo();
      } catch (e) {
        console.error('[TodoManagement] onAddTodo threw', e);
      }
    }
  };

  const handleToggleTodo = (todoId: string, completed: boolean) => {
    if (onToggleTodo) {
      onToggleTodo(todoId, completed);
    }
  };

  const visibleTodos = isTodosExpanded ? todos : todos.slice(0, initialVisibleCount);
  const hasMoreTodos = todos.length > initialVisibleCount;

  const content = (
    <View style={[styles.container, !showCard && styles.containerNoCard, style]}>
      <View style={styles.header}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View style={{ marginRight: 8 }}>
            <Icon name="check-circle" size={18} color={MODULE_COLORS.travel} />
          </View>
          <Text style={styles.title}>{t('travelDetail.content.todoList')}</Text>
        </View>
        {canAddTodo && onAddTodo && (
          <TouchableOpacity style={styles.addButton} onPress={handleAddTodo}>
            <Icon name="add" size={16} color={MODULE_COLORS.travel} />
            <Text style={styles.addButtonText}>{t('travelDetail.content.add')}</Text>
          </TouchableOpacity>
        )}
      </View>

      {isLoading ? (
        // 加载中 - 不显示任何内容，避免闪烁
        null
      ) : todos.length > 0 ? (
        <View style={[styles.todoList, !showCard && styles.todoListNoCard, todoListStyle]}>
          {visibleTodos.map((todo, index) => {
            // 兼容不同的数据格式
            const title = todo.title || (todo as any).text;
            const creatorName = todo.creator?.display_name || todo.created_by;
            const createdDate = todo.createdAt || todo.created_at;
            const isAd = todo.metadata?.isAd === true;
            const adCta = todo.metadata?.cta || '查看详情';

            // prepare meta text to avoid creating stray text nodes under View (react-native-web requires text inside <Text>)
            const formattedDate = createdDate ? new Date(createdDate).toLocaleDateString('zh-CN') : '';
            const metaText = `${creatorName || t('travelDetail.content.unknownUser')}${formattedDate ? ` • ${formattedDate}` : ''}`;

            return (
              <View key={todo.id || index} style={[styles.todoItemWrapper, isAd && styles.todoItemWrapperAd, todo.completed && styles.todoItemCompleted]}>
                {/* 上部: 勾选框 + 内容 */}
                <View style={styles.todoItemRow}>
                  {/* 左侧: 勾选框 */}
                  <TouchableOpacity
                    style={styles.todoCheckbox}
                    onPress={() => handleToggleTodo(todo.id, todo.completed)}
                    activeOpacity={0.7}
                    disabled={!onToggleTodo}
                  >
                    {todo.completed ? (
                      <Icon name="check-circle" size={20} color={MODULE_COLORS.travel} />
                    ) : (
                      <Icon name="radio-button-unchecked" size={20} color="#D1D5DB" />
                    )}
                  </TouchableOpacity>

                  {/* 右侧: 待办内容 */}
                  <View style={styles.todoContent}>
                    <Text
                      style={[
                        styles.todoText,
                        todo.completed && styles.todoTextCompleted,
                      ]}
                      numberOfLines={2}
                    >
                      {title}
                    </Text>
                    {todo.description && (
                      <Text style={styles.todoDescription} numberOfLines={2}>
                        {todo.description}
                      </Text>
                    )}
                    {(creatorName || createdDate) && (
                      <View style={styles.todoMetaContainer}>
                        <Text style={styles.todoMeta}>
                          {metaText}
                        </Text>
                        {todo.completed && (
                          <Text style={styles.todoCompletedBadge}>
                            ✓ {t('travelDetail.content.completed')}
                          </Text>
                        )}
                      </View>
                    )}
                  </View>

                  {/* 右側: 廣告 CTA（與內容同行） */}
                  {isAd && todo.metadata?.adUrl && onAdClick && (
                    <TouchableOpacity
                      style={styles.adCtaInline}
                      onPress={() => {
                        if (todo.metadata?.adUrl && todo.metadata?.adPartner) {
                          onAdClick(todo.metadata.adUrl, todo.metadata.adPartner, todo.id);
                        }
                      }}
                      activeOpacity={0.8}
                    >
                      <Text style={styles.adCtaTextInline}>{adCta}</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            );
          })}

          {hasMoreTodos && (
            <TouchableOpacity
              style={styles.expandButton}
              onPress={() => setIsTodosExpanded(!isTodosExpanded)}
            >
              <Text style={styles.expandButtonText}>
                {isTodosExpanded
                  ? t('travelDetail.content.collapse')
                  : t('travelDetail.content.expandWithCount', { count: todos.length - initialVisibleCount })
                }
              </Text>
              <Icon
                name={isTodosExpanded ? 'expand-less' : 'expand-more'}
                size={16}
                color={MODULE_COLORS.travel}
              />
            </TouchableOpacity>
          )}
        </View>
      ) : (
        <View style={styles.emptyState}>
          <Icon name="check-circle" size={48} color="#ccc" />
          <Text style={styles.emptyText}>{t('travelDetail.content.noTodos')}</Text>
          <Text style={styles.emptySubtext}>{t('travelDetail.content.noTodosSubtext')}</Text>
        </View>
      )}
    </View>
  );

  return content;
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(255,102,0,0.05)',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: MODULE_COLORS.travel,
  },
  addButtonText: {
    marginLeft: 4,
    fontSize: 12,
    color: MODULE_COLORS.travel,
    fontWeight: '500',
  },
  todoList: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
  },
  todoListNoCard: {
    backgroundColor: 'transparent',
    borderRadius: 0,
    padding: 0,
  },
  todoItemWrapper: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  todoItemWrapperAd: {
    backgroundColor: '#FFF7ED', // 浅橙色背景
    marginHorizontal: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderBottomWidth: 0,
    marginBottom: 8,
    paddingBottom: 16, // 增加底部空间给按钮
  },
  todoItemRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  todoItemCompleted: {
    opacity: 0.7,
  },
  todoCheckbox: {
    marginRight: 12,
    marginTop: 2,
    paddingVertical: 4,
  },
  todoContent: {
    flex: 7,
    paddingRight: 8,
  },
  todoText: {
    fontSize: 15,
    color: '#374151',
    lineHeight: 20,
  },
  todoTextCompleted: {
    textDecorationLine: 'line-through',
    color: '#9CA3AF',
  },
  todoDescription: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
    lineHeight: 18,
  },
  todoMetaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
    flexWrap: 'wrap',
  },
  todoMeta: {
    fontSize: 11,
    color: '#9CA3AF',
  },
  todoCompletedBadge: {
    fontSize: 10,
    color: MODULE_COLORS.travel,
    marginLeft: 8,
    fontWeight: '500',
  },
  adCtaButtonBottom: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: MODULE_COLORS.travel,
    alignSelf: 'flex-end',
    minWidth: 140,
  },
  adCtaInline: {
    flex: 3,
    alignSelf: 'center',
    marginLeft: 12,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: MODULE_COLORS.travel,
    minWidth: 80,
    maxWidth: 140,
    justifyContent: 'center',
    alignItems: 'center',
  },
  adCtaTextInline: {
    fontSize: 14,
    color: MODULE_COLORS.travel,
    fontWeight: '600',
    textAlign: 'center',
  },
  adCtaTextBottom: {
    fontSize: 14,
    color: MODULE_COLORS.travel,
    fontWeight: '600',
  },
  expandButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    marginTop: 8,
  },
  expandButtonText: {
    fontSize: 14,
    color: MODULE_COLORS.travel,
    fontWeight: '500',
    marginRight: 4,
  },
  emptyState: {
    alignItems: 'center',
    padding: 24,
    backgroundColor: '#fff',
    borderRadius: 8,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#6B7280',
    marginTop: 12,
  },
  emptySubtext: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 4,
    textAlign: 'center',
  },
});

export default TodoManagement;